import React from "react";
import { getDisabledStateFromLifecycleStatuses } from "../../utilities/lifecycleStatus";
import {
	IconArchive,
	IconArchiveOff,
	IconCopy,
	IconCursorText,
	IconDownload,
	IconFileBroken,
	IconFileTypePdf,
	IconLink,
	IconTextScan2,
	IconTool,
	IconTrashOff,
	IconTrashX,
	IconUpload,
} from "@tabler/icons-react";
import { makeAdminDetailsMenuItem } from "../Admin/AdminDetailsMenuItem";
import {
	getCheckoutMenuItems,
	getLiteraDisabledHoverText,
	getInlineDisabledHoverText,
	splitFilename,
	convertToPDFAllowed,
	ocrOnDemandAllowed,
	ocrIsDisabledForDocument,
} from "../../utilities/utilities";

// Pure function to generate compare section items
const getCompareMenuItems = ({
	id,
	filename,
	isFileTypeCompatibleWithLitera,
	isFileTypeCompatibleWithInline,
	literaDisabledAndHoverText,
	inlineDisabledAndHoverText,
	onAddToLiteraCompare,
	onAddToInlineCompare,
}) => {
	const compareItems = [];

	if (isFileTypeCompatibleWithLitera) {
		compareItems.push({
			text: "Compare in Litera",
			disabled: !!literaDisabledAndHoverText,
			disabledHoverText: literaDisabledAndHoverText,
			onSelect: () => {
				onAddToLiteraCompare({
					docID: id,
					filename: filename,
					type: "litera",
				});
			},
			iconComponent: (
				<IconFileBroken
					color={"var(--lxw-main-500"}
					size={16}
					style={{ verticalAlign: "sub" }}
				/>
			),
		});
	}

	if (isFileTypeCompatibleWithInline) {
		compareItems.push({
			text: "Compare in LexWorkplace",
			disabled: !!inlineDisabledAndHoverText,
			disabledHoverText: inlineDisabledAndHoverText,
			onSelect: () => {
				onAddToInlineCompare({
					docID: id,
					filename: filename,
					type: "inline",
				});
			},
			iconComponent: (
				<IconFileBroken
					color={"var(--lxw-main-500"}
					size={16}
					style={{ verticalAlign: "sub" }}
				/>
			),
		});
	}

	// Only return section header and divider if there are compare items
	if (compareItems.length > 0) {
		return [
			{ divider: true },
			{ sectionHeader: true, text: "Compare" },
			...compareItems,
		];
	}

	return [];
};

const DocumentContextMenu = ({
	document,
	permissions,
	callbacks,
	location,
	userRights,
	isLexworkplaceAdmin,
	tenantID,
}) => {
	const {
		id,
		filename,
		documentLifecycleStatus,
		lockedBySub,
		lockStatus,
		lockedBy,
		isFileTypeCompatibleWithLitera,
		isFileTypeCompatibleWithInline,
	} = document;

	const documentLifecycleStatusObject = getDisabledStateFromLifecycleStatuses(
		{
			documentLifecycleStatus,
		}
	);

	const {
		matterOrFolderLifecycleStatusObject,
		archiveAccess,
		cognitoSub,
		launcherInstalled,
		canOverrideDocumentLocks,
		isOcrEnabled,
		isTenantOverOcrLimit,
		isLiteraCompareVisible,
		isLiteraCompareEnabled,
		isInlineCompareEnabled,
		docCompareType,
		firstDocIdInDocumentCompare,
	} = permissions;

	const {
		onDownload,
		onRename,
		onDuplicate,
		onUploadNewVersion,
		onConvertToPDF,
		onRequestOCR,
		onAddToLiteraCompare,
		onAddToInlineCompare,
		onSetLockStatus,
		onCopyDocLink,
		onDelete,
		onRestore,
		onArchive,
		onUnarchive,
	} = callbacks;

	const { matterID, apiBasepath, originalPrefix } = location;

	const documentActionsAreDisabled =
		documentLifecycleStatusObject.disabled ||
		matterOrFolderLifecycleStatusObject.disabled;
	const documentActionsAreDisabledHoverText =
		matterOrFolderLifecycleStatusObject.disabled
			? matterOrFolderLifecycleStatusObject.disabledText
			: documentLifecycleStatusObject.disabled
				? documentLifecycleStatusObject.disabledText
				: "";
	const docArchiveAccess = archiveAccess
		? documentLifecycleStatusObject.IN_ARCHIVE
			? archiveAccess.unarchive
			: archiveAccess.archive
		: null;

	const documentLockedBySomeoneElse =
		lockedBySub && lockedBySub !== cognitoSub;

	const documentIsInUse =
		lockedBySub && (lockStatus === 1 || lockStatus === 3);

	const isAlreadySelectedForDocCompare = firstDocIdInDocumentCompare === id;

	const canAddToLiteraCompare =
		!docCompareType || docCompareType === "litera";
	const canAddToInlineCompare =
		!docCompareType || docCompareType === "inline";

	const literaDisabledAndHoverText = isFileTypeCompatibleWithLitera
		? getLiteraDisabledHoverText({
				isLiteraCompareVisible,
				isLiteraCompareEnabled,
				isAlreadySelectedForDocCompare,
				canAddToLiteraCompare,
				launcherInstalled,
				documentActionsAreDisabled,
				documentActionsAreDisabledHoverText,
			})
		: null;

	const inlineDisabledAndHoverText = isFileTypeCompatibleWithInline
		? getInlineDisabledHoverText({
				isInlineCompareEnabled,
				isAlreadySelectedForDocCompare,
				canAddToInlineCompare,
				documentActionsAreDisabled,
				documentActionsAreDisabledHoverText,
			})
		: null;

	const ocrDisabledInfo = ocrIsDisabledForDocument({
		lockedBySub,
		cognitoSub,
		isOcrEnabled,
		isTenantOverOcrLimit,
		documentActionsAreDisabled,
		documentActionsAreDisabledHoverText,
		userRights,
	});

	const menuItems = [
		isLexworkplaceAdmin
			? makeAdminDetailsMenuItem(`/admin/tenant/${tenantID}/docs/${id}`)
			: null,
		{ sectionHeader: true, text: "General" },
		{
			text: "Document Properties",
			href: "/docs/" + id,
			iconComponent: (
				<IconTool
					color={"var(--lxw-main-500"}
					size={16}
					style={{ verticalAlign: "sub" }}
				/>
			),
		},
		{
			text: "Copy Internal Document URL",
			onSelect: () => onCopyDocLink(id),
			iconComponent: (
				<IconLink
					color={"var(--lxw-main-500"}
					size={16}
					style={{ verticalAlign: "sub" }}
				/>
			),
		},
		{ divider: true },
		{ sectionHeader: true, text: "Document Actions" },
		{
			text: "Rename",
			onSelect: () => onRename(id, filename),
			iconComponent: (
				<IconCursorText
					color={"var(--lxw-main-500"}
					size={16}
					style={{ verticalAlign: "sub" }}
				/>
			),
		},
		{
			text: "Download a Copy",
			onSelect: () => onDownload(id),
			iconComponent: (
				<IconDownload
					color={"var(--lxw-main-500"}
					size={16}
					style={{ verticalAlign: "sub" }}
				/>
			),
		},
		{
			text: "Upload New Version",
			type: "single-upload",
			uniqueId: id,
			fileListChangeHandler: (files, callback) => {
				onUploadNewVersion(document, files, callback, {
					matterID,
					basepath: apiBasepath,
				});
			},
			disabled: documentIsInUse || documentActionsAreDisabled,
			disabledHoverText: documentLockedBySomeoneElse
				? "This document is locked by someone else."
				: documentActionsAreDisabledHoverText,
			uploadAccept: (() => {
				const { extension } = splitFilename(filename);
				return extension ? "." + extension : "*";
			})(),
			iconComponent: (
				<IconUpload
					color={"var(--lxw-main-500"}
					size={16}
					style={{ verticalAlign: "sub" }}
				/>
			),
		},
		{
			text: "Duplicate",
			onSelect: () => onDuplicate({ [id]: document }),
			disabled: documentActionsAreDisabled,
			disabledHoverText: documentActionsAreDisabledHoverText,
			iconComponent: (
				<IconCopy
					color={"var(--lxw-main-500"}
					size={16}
					style={{ verticalAlign: "sub" }}
				/>
			),
		}, // Checkout menu items
		...getCheckoutMenuItems({
			lockedByDisplay: lockedBy,
			lockedBySub: lockedBySub,
			lockStatus: lockStatus,
			documentID: id,
			basepath: apiBasepath,
			userSub: cognitoSub,
			fn: onSetLockStatus,
			matterID: matterID,
			userCanOverrideDocumentLocks: canOverrideDocumentLocks,
			isDisabled: documentActionsAreDisabled,
			disabledHoverText: documentActionsAreDisabledHoverText,
		}),
		convertToPDFAllowed(filename)
			? {
					text: "Convert to PDF",
					disabled:
						documentLockedBySomeoneElse ||
						documentActionsAreDisabled,
					disabledHoverText: documentLockedBySomeoneElse
						? "This document is locked by someone else."
						: documentActionsAreDisabledHoverText,
					onSelect: () => {
						onConvertToPDF({
							sourceDocumentID: id,
							fileToConvert: filename,
							matterID,
							actionType: "createPDF",
							basepath: originalPrefix,
						});
					},
					iconComponent: (
						<IconFileTypePdf
							color={"var(--lxw-main-500"}
							size={16}
							style={{ verticalAlign: "sub" }}
						/>
					),
				}
			: "",
		ocrOnDemandAllowed(filename)
			? {
					text: "Request OCR",
					disabled: ocrDisabledInfo.disabled,
					disabledHoverText: ocrDisabledInfo.disabledHoverText,
					onSelect: () => onRequestOCR(id, filename),
					iconComponent: (
						<IconTextScan2
							color={"var(--lxw-main-500"}
							size={16}
							style={{ verticalAlign: "sub" }}
						/>
					),
				}
			: "",
		// Compare section - only included if compare options are available
		...getCompareMenuItems({
			id,
			filename,
			isFileTypeCompatibleWithLitera,
			isFileTypeCompatibleWithInline,
			literaDisabledAndHoverText,
			inlineDisabledAndHoverText,
			onAddToLiteraCompare,
			onAddToInlineCompare,
		}),
		{ divider: true },
		{ sectionHeader: true, text: "Archive + Trash" },
		onArchive || onUnarchive
			? {
					text: documentLifecycleStatusObject.IN_ARCHIVE
						? "Unarchive File"
						: "Archive File",
					disabled:
						matterOrFolderLifecycleStatusObject.disabled ||
						(!!lockedBy &&
							!documentLifecycleStatusObject.IN_ARCHIVE) ||
						docArchiveAccess.disabled,
					disabledHoverText: documentLockedBySomeoneElse
						? "This Document is locked by someone else."
						: !documentLifecycleStatusObject.IN_ARCHIVE &&
							  !!lockedBy
							? "You cannot move this Document to the Archive while it is in use and/or checked out."
							: documentActionsAreDisabledHoverText ||
								(docArchiveAccess.disabled
									? docArchiveAccess.disabledText
									: ""),
					onSelect: () =>
						documentLifecycleStatusObject.IN_ARCHIVE
							? onUnarchive([id], matterID, apiBasepath)
							: onArchive([id], matterID, apiBasepath),
					iconComponent: documentLifecycleStatusObject.IN_ARCHIVE ? (
						<IconArchiveOff
							color={"var(--lxw-main-500"}
							size={16}
							style={{ verticalAlign: "sub" }}
						/>
					) : (
						<IconArchive
							color={"var(--lxw-main-500"}
							size={16}
							style={{ verticalAlign: "sub" }}
						/>
					),
				}
			: null,
		{
			text: documentLifecycleStatusObject.IN_TRASH
				? "Restore File from Trash Bin"
				: "Move File to Trash Bin",
			disabled:
				matterOrFolderLifecycleStatusObject.disabled ||
				(!!lockedBy && !documentLifecycleStatusObject.IN_TRASH) ||
				!!(
					documentLifecycleStatusObject.IN_ARCHIVE &&
					docArchiveAccess?.disabled
				),
			disabledHoverText: documentLockedBySomeoneElse
				? "This Document is locked by someone else."
				: !documentLifecycleStatusObject.IN_TRASH && !!lockedBy
					? "You cannot move this Document to the Trash Bin while it is in use and/or checked out."
					: documentLifecycleStatusObject.IN_ARCHIVE &&
						  docArchiveAccess?.disabled
						? docArchiveAccess.disabledText
						: documentActionsAreDisabledHoverText,
			onSelect: () =>
				documentLifecycleStatusObject.IN_TRASH
					? onRestore([id], matterID, apiBasepath)
					: onDelete([id], matterID, apiBasepath),
			iconComponent: documentLifecycleStatusObject.IN_TRASH ? (
				<IconTrashOff
					color={"var(--lxw-main-500"}
					size={16}
					style={{ verticalAlign: "sub" }}
				/>
			) : (
				<IconTrashX
					color={"var(--lxw-main-500"}
					size={16}
					style={{ verticalAlign: "sub" }}
				/>
			),
		},
	].filter(Boolean); // Remove empty strings

	return menuItems;
};

export default DocumentContextMenu;
